import { useState } from "react";
import { Package } from "lucide-react";

/**
 * LinkPreviewThumb — renders a shop-item / gift-item preview thumbnail with
 * a graceful fallback chain:
 *
 *   1. `overridePath` (author-uploaded via /api/upload) → served directly
 *      through /api/files/{path}. Bulletproof for retailers that block
 *      scraping (Amazon / Overstock).
 *   2. `src` (retailer og:image URL) → streamed through our
 *      /api/link-preview/image passthrough proxy so hotlink-blocked
 *      retailers still render.
 *   3. Placeholder tile with a soft package icon.
 *
 * Nothing is stored on our servers for step 2 — the proxy streams bytes
 * memory-only.
 */
export function LinkPreviewThumb({ src, overridePath, alt = "", size = "md", testId }) {
  const [errored, setErrored] = useState(false);
  const dims = size === "sm" ? "w-12 h-12" : size === "lg" ? "w-24 h-24" : "w-16 h-16";
  const iconSize = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-7 h-7" : "w-5 h-5";
  const overrideUrl = overridePath
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${overridePath}`
    : null;
  const proxied = src
    ? `${process.env.REACT_APP_BACKEND_URL}/api/link-preview/image?url=${encodeURIComponent(src)}`
    : null;
  const finalSrc = overrideUrl || proxied;

  if (!finalSrc || errored) {
    return (
      <div
        data-testid={testId ? `${testId}-placeholder` : undefined}
        className={`${dims} shrink-0 rounded-lg bg-[#B79256]/12 border border-[#B79256]/25 flex items-center justify-center`}
        aria-hidden={!alt}
        role={alt ? "img" : undefined}
        aria-label={alt || undefined}
      >
        <Package className={`${iconSize} text-[#B79256]/70`} strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <img
      src={finalSrc}
      alt={alt}
      data-testid={testId}
      loading="lazy"
      onError={() => setErrored(true)}
      className={`${dims} shrink-0 rounded-lg object-cover bg-white border border-[#B79256]/25`}
    />
  );
}
